import { type FC } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { _isNotEmpty } from '@/littledash';
import { NoDataMessage } from '@/components/NoDataCard/NoDataCard';
import Spinner from '@/components/UI/Spinner/Spinner';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import AgentCard from '@/components/Agents/AgentCard/AgentCard';
import RecentSessionsPanel from '@/components/Agents/RecentSessionsPanel/RecentSessionsPanel';
import { LuSparkle } from 'react-icons/lu';
import styles from './AgentsPage.module.scss';

/**
 * Top-level page displaying available AI agents and a recent sessions panel.
 * Fetches agent definitions from GET /api/v1/agents.
 */
const AgentsPage: FC = () => {
  const { loading, response } = useApiHook({
    endpoint: 'GET /api/v1/agents',
    options: {
      invokeOnInit: true,
      onError: {
        slug: 'agents-page',
        toast: false,
        throw: false,
      },
    },
  });

  const agents = response?.type === 'success' ? response.body : [];

  return (
    <div className="ph4 pb4" data-test-component="AgentsPage" data-test-element="container">
      <div className="pt3 pb4" data-test-element="header">
        <div className={styles.titleRow}>
          <h1 className="f3 normal ma0" data-test-element="title">
            In Vivo AI
          </h1>
          <LuSparkle size={20} className="blue" />
        </div>
        <p className="f5 lh-copy dark-gray mt3 mb0" data-test-element="subtitle">
          Purpose-built AI agents for your In Vivo studies. Each agent works with your live study data and returns
          results you can review and apply directly.
        </p>
      </div>

      <Tabs>
        <div className="bb b--moon-gray">
          <Tab className="f4" testId="agents-tab">
            Agents
          </Tab>
          <Tab className="f4" testId="sessions-tab" disableNavigation>
            Sessions
          </Tab>
        </div>

        <Panel className="pt4">
          <div className={styles.content}>
            {loading ? (
              <div className="flex justify-center items-center w-100 pv5" data-testid="agents-page-loading">
                <Spinner size="default" color="blue" />
              </div>
            ) : _isNotEmpty(agents) ? (
              <div className={styles.grid}>
                {agents.map((agent) => (
                  <AgentCard key={agent.api_id} agent={agent} />
                ))}
              </div>
            ) : (
              <div className="w-100">
                <NoDataMessage
                  title="No agents available"
                  text="AI agents will appear here when enabled for your team."
                  dark
                />
              </div>
            )}
            <RecentSessionsPanel />
          </div>
        </Panel>

        <Panel>
          <NoDataMessage title="No sessions yet" text="Sessions from your agent runs will appear here." dark />
        </Panel>
      </Tabs>
    </div>
  );
};

export default AgentsPage;
